import axios from "axios";
import React, { Suspense, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import LineIcon from "react-lineicons";
import Layout from "../components/Layout";
import Sectiontitle from "../components/Sectiontitle";
import Spinner from "../components/Spinner";
import Testimonial from "../components/Testimonial";
import Slider from "react-slick";

interface ServiceOffering {
  id: number;
  title: string;
  icon: string;
  shortDescription: string;
  details: string;
  deliverables: string[];
  timeline?: string;
  idealFor: string[];
}

interface ServicesPageData {
  headline: string;
  introduction: string;
  valueProposition: string;
  services: ServiceOffering[];
  callToAction: string;
}

interface Review {
  id: string | number;
  [key: string]: unknown;
}

const Services: React.FC = () => {
  const [servicesData, setServicesData] = useState<ServicesPageData>({
    headline: "",
    introduction: "",
    valueProposition: "",
    services: [],
    callToAction: "",
  });
  const [reviews, setReviews] = useState<Review[]>([]);
  const [expandedService, setExpandedService] = useState<number | null>(null);

  const sliderSettings = {
    dots: false,
    infinite: true,
    arrows: false,
    speed: 500,
    slidesToShow: 2,
    slidesToScroll: 2,
    autoplay: true,
    autoplaySpeed: 6000,
    pauseOnHover: true,
    adaptiveHeight: true,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  useEffect(() => {
    axios.get<ServicesPageData>("/api/services").then((response) => {
      setServicesData(response.data);
    });
    axios.get<Review[]>("/api/reviews").then((response) => {
      setReviews(response.data);
    });
  }, []);

  const toggleServiceDetails = (serviceId: number) => {
    setExpandedService(expandedService === serviceId ? null : serviceId);
  };

  return (
    <Layout>
      <Helmet>
        <title>Services - Nathan Hensher</title>
        <meta
          name="description"
          content="Nathan Hensher - Frontend Consultancy & MVP Development Services"
        />
      </Helmet>
      <Suspense fallback={<Spinner />}>
        <div className="mi-about-area mi-section mi-padding-top">
          <div className="container">
            <Sectiontitle title="Services" />
            <div className="row">
              <div className="col-lg-12">
                <div className="mi-about-content">
                  <h3 className="color-theme">{servicesData.headline}</h3>
                  <p className="mt-30">{servicesData.introduction}</p>
                  <p className="mt-20">{servicesData.valueProposition}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mi-service-area mi-section mi-padding-top">
          <div className="container">
            <Sectiontitle title="What I Offer" />
            <div className="mi-service-wrapper">
              <div className="row mt-30-reverse">
                {servicesData.services.map((service) => (
                  <div
                    className="col-lg-6 col-md-6 col-12 mt-30"
                    key={service.id}
                  >
                    <div
                      className="mi-service mi-service-clickable"
                      onClick={() => toggleServiceDetails(service.id)}
                      style={{ cursor: "pointer", height: "100%" }}
                    >
                      <span className="mi-service-icon">
                        <LineIcon name={service.icon} />
                      </span>
                      <h5>{service.title}</h5>
                      <p>{service.shortDescription}</p>
                      {service.timeline && (
                        <span className="mi-service-timeline">
                          <strong>Timeline:</strong> {service.timeline}
                        </span>
                      )}

                      {expandedService === service.id && (
                        <div className="mi-service-expanded mt-20">
                          <p className="mi-service-details">
                            {service.details}
                          </p>

                          <div className="mi-service-deliverables mt-15">
                            <h6>Deliverables:</h6>
                            <ul>
                              {service.deliverables.map(
                                (deliverable, index) => (
                                  <li key={index}>{deliverable}</li>
                                )
                              )}
                            </ul>
                          </div>

                          <div className="mi-service-ideal-for mt-15">
                            <h6>Ideal For:</h6>
                            <ul>
                              {service.idealFor.map((ideal, index) => (
                                <li key={index}>{ideal}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}

                      <span className="mi-service-toggle mt-15">
                        {expandedService === service.id
                          ? "Show less"
                          : "Learn more"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="mi-about-area mi-section mi-padding-top">
          <div className="container">
            <Sectiontitle title="Why Work With Me" />
            <div className="row">
              <div className="col-lg-4 col-md-6 col-12 mt-30">
                <div className="mi-service">
                  <span className="mi-service-icon">
                    <LineIcon name="timer" />
                  </span>
                  <h5>Rapid Delivery</h5>
                  <p>
                    Core component libraries and frontend architecture delivered
                    in 4 weeks, enabling your team to build features
                    immediately.
                  </p>
                </div>
              </div>
              <div className="col-lg-4 col-md-6 col-12 mt-30">
                <div className="mi-service">
                  <span className="mi-service-icon">
                    <LineIcon name="code-alt" />
                  </span>
                  <h5>Production Quality</h5>
                  <p>
                    No throwaway code. Everything I build is designed to scale
                    with your business, reducing future refactoring costs.
                  </p>
                </div>
              </div>
              <div className="col-lg-4 col-md-6 col-12 mt-30">
                <div className="mi-service">
                  <span className="mi-service-icon">
                    <LineIcon name="users" />
                  </span>
                  <h5>Knowledge Transfer</h5>
                  <p>
                    I don't just build and leave. Your team receives
                    comprehensive documentation and training to maintain and
                    extend the codebase.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mi-review-area mi-section mi-padding-top">
          <div className="container">
            <Sectiontitle title="What Colleagues Say" />
            <div className="row justify-content-center">
              <div className="col-12">
                <Slider className="mi-testimonial-slider" {...sliderSettings}>
                  {reviews.map((review) => (
                    <Testimonial key={review.id} content={review as never} />
                  ))}
                </Slider>
              </div>
            </div>
          </div>
        </div>

        <div className="mi-about-area mi-section mi-padding-top mi-padding-bottom">
          <div className="container">
            <div className="row">
              <div className="col-lg-12 text-center">
                <div className="mi-about-content">
                  <h3 className="color-theme">Let's Build Something Great</h3>
                  <p className="mt-20">{servicesData.callToAction}</p>
                  <Link to="/contact" className="mi-button mt-30">
                    Get in Touch
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Suspense>
    </Layout>
  );
};

export default Services;
